import { FC, useState, useEffect, useRef } from 'react';
import { SparkleIcon, PaperPlaneTiltIcon, LightningIcon, BroomIcon, UsersThreeIcon, PlusIcon } from '@phosphor-icons/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  ZAxis,
} from 'recharts';
import axios from 'axios';
import { useAppSelector } from '../../store';
import { AthleteCard, AthleteCardData } from './components/AthleteCard';
import { TrendChart, TrendChartData } from './components/TrendChart';
import { FollowUps } from './components/FollowUps';
import styles from './AiCoachPage.module.scss';

interface ConversationItem {
  question: string;
  answer: string;
  model: string;
  fallbacks: number;
  elapsedMs: number;
  toolCalls?: number;
  toolsUsed?: string[];
  scope?: { userType: string };
}

type ChartBlock =
  | { type: 'bar'; title: string; unit: string; data: { name: string; value: number }[] }
  | { type: 'line'; title: string; unit: string; data: { name: string; value: number }[] }
  | { type: 'trend'; data: TrendChartData }
  | {
      type: 'comparison';
      title: string;
      unit: string;
      data: { name: string; current: number; baseline: number; delta: number }[];
    }
  | {
      type: 'distribution';
      title: string;
      unit: string;
      data: { label: string; count: number; severity?: 'critical' | 'warning' | 'normal' | 'good' }[];
    }
  | {
      type: 'scatter';
      title: string;
      unit: string;
      xLabel?: string;
      yLabel?: string;
      data: { name: string; x: number; y: number; highlight?: boolean }[];
    };

interface AnswerBlocks {
  text: string;
  charts: ChartBlock[];
  athletes: AthleteCardData[];
  followups: string[];
}

const SUGGESTIONS = [
  'Quali atleti hanno HRV in calo questa settimana?',
  'Mostra atleti con fastidi al ginocchio negli ultimi 30 giorni',
  'Chi non ha compilato il questionario Hooper di recente?',
  'Quali atleti hanno carico esterno alto e recovery basso?',
  'Genera un briefing dei 5 atleti che meritano più attenzione oggi',
];

export const AiCoachPage: FC = () => {
  useAppSelector((s) => s.me); // mantiene re-render su cambio entity
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState<ConversationItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, loading]);

  const send = async (q?: string) => {
    const finalQ = (q ?? question).trim();
    if (!finalQ || loading) return;
    setError(null);
    setLoading(true);
    setQuestion('');
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL ?? 'http://localhost:3014/api';
      // Stripping inline chart blocks dall'history riduce token e rumore
      const history = conversation.slice(-6).map((c) => ({
        question: c.question,
        answer: c.answer.replace(/<chart[^>]*>[\s\S]*?<\/chart>/g, '').replace(/<athlete-card>[\s\S]*?<\/athlete-card>/g, '').replace(/<followups>[\s\S]*?<\/followups>/g, '').trim(),
      }));
      const { data } = await axios.post(`${baseUrl}/ai-router/ask`, {
        question: finalQ,
        task: 'qa',
        history,
      });
      setConversation((c) => [
        ...c,
        {
          question: finalQ,
          answer: data.answer,
          model: data.model,
          fallbacks: data.fallbacks,
          elapsedMs: data.elapsedMs,
          toolCalls: data.toolCalls,
          toolsUsed: data.toolsUsed,
          scope: data.scope,
        },
      ]);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.minimalHeader}>
        <div className={styles.minimalIcon}>
          <SparkleIcon size={16} weight="fill" />
        </div>
        <div className={styles.minimalTitleBox}>
          <div className={styles.minimalTitle}>AI Coach</div>
          <div className={styles.minimalSubtitle}>
            Conversazione · Tutta la squadra · oggi
          </div>
        </div>
        {conversation.length > 0 ? (
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={() => {
              setConversation([]);
              setError(null);
            }}
            title="Pulisci la conversazione"
          >
            <PlusIcon size={14} weight="bold" />
            Nuova
          </button>
        ) : (
          <button type="button" className={styles.ghostBtn} disabled>
            <BroomIcon size={14} weight="bold" />
            Pulisci chat
          </button>
        )}
      </header>

      <div className={styles.wrapper}>
      {conversation.length === 0 && !loading && (
        <div className={styles.emptyHero}>
          <div className={styles.emptyIcon}>
            <SparkleIcon size={28} weight="fill" />
          </div>
          <h2 className={styles.emptyTitle}>Cosa vuoi sapere sul tuo team?</h2>
          <p className={styles.emptySubtitle}>
            Chiedi qualsiasi cosa sui tuoi atleti — HRV, carico, fastidi, recovery, storico.
          </p>
          <div className={styles.emptyGrid}>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                className={styles.emptyCard}
                onClick={() => send(s)}
              >
                <LightningIcon size={14} weight="fill" />
                <span>{s}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={styles.conversation}>
        {conversation.map((c, idx) => {
          const { text, charts, athletes, followups } = parseAnswerBlocks(c.answer);
          return (
            <div key={idx} className={styles.qaBlock}>
              <div className={styles.questionRow}>
                <div className={styles.qBubble}>{c.question}</div>
              </div>
              <div className={styles.answerRow}>
                <div className={styles.aBubble}>
                  {text && <div className={styles.aText}>{renderMarkdown(text)}</div>}
                  {athletes.length > 0 && (
                    <div className={styles.athleteList}>
                      {athletes.map((a, i) => (
                        <AthleteCard key={i} athlete={a} />
                      ))}
                    </div>
                  )}
                  {charts.map((ch, i) => (
                    <ChartRender key={i} chart={ch} />
                  ))}
                  {followups.length > 0 && <FollowUps questions={followups} onAsk={send} />}
                  <div className={styles.aMeta}>
                    {typeof c.toolCalls === 'number' && (
                      <>
                        <span>
                          <strong>{c.toolCalls}</strong> {c.toolCalls === 1 ? 'query' : 'query'} eseguite
                        </span>
                        <span>·</span>
                      </>
                    )}
                    <span>{(c.elapsedMs / 1000).toFixed(1)}s</span>
                    {c.scope?.userType && (
                      <>
                        <span>·</span>
                        <span title={`Scope dati: ${c.scope.userType}`}>scope: {c.scope.userType}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {loading && (
          <div className={styles.qaBlock}>
            <div className={styles.answerRow}>
              <div className={styles.aBubble}>
                <div className={styles.thinking}>
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                  <span className={styles.thinkingText}>
                    AI sta elaborando i dati di tutti i tuoi atleti...
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        {error && <div className={styles.error}>{error}</div>}
        <div ref={bottomRef} />
      </div>

      <div className={styles.composerWrap}>
        {conversation.length > 0 && !loading && (
          <div className={styles.composerSuggestions}>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className={styles.composerSuggestionChip}
                onClick={() => send(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <form
          className={styles.composer}
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <button type="button" className={styles.scopeChip} title="Scope dati">
            <UsersThreeIcon size={12} weight="bold" /> Tutta la squadra
          </button>
          <input
            className={styles.composerInput}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Chiedi qualsiasi cosa sui tuoi atleti..."
            disabled={loading}
            autoFocus
          />
          <button
            type="submit"
            className={styles.composerSend}
            disabled={loading || !question.trim()}
          >
            <PaperPlaneTiltIcon size={14} weight="fill" />
            {loading ? 'Elaborazione...' : 'Invia'}
          </button>
        </form>
        <div className={styles.disclaimer}>
          AI Coach può sbagliare · verifica sempre i dati prima di prendere decisioni cliniche
        </div>
      </div>
      </div>
    </div>
  );
};

const CHART_REGEX = /<chart\s+([^>]*)>([\s\S]*?)<\/chart>/g;
const ATHLETE_CARD_REGEX = /<athlete-card>([\s\S]*?)<\/athlete-card>/g;
const FOLLOWUPS_REGEX = /<followups>([\s\S]*?)<\/followups>/g;

function parseAnswerBlocks(answer: string): AnswerBlocks {
  const charts: ChartBlock[] = [];
  const athletes: AthleteCardData[] = [];
  const followups: string[] = [];

  let text = answer;

  // Athlete cards
  text = text.replace(ATHLETE_CARD_REGEX, (_, body: string) => {
    try {
      const json = JSON.parse(body.trim());
      if (Array.isArray(json)) {
        athletes.push(...json);
      } else if (json && typeof json === 'object') {
        athletes.push(json);
      }
    } catch {}
    return '';
  });

  // Follow-ups
  text = text.replace(FOLLOWUPS_REGEX, (_, body: string) => {
    try {
      const json = JSON.parse(body.trim());
      if (Array.isArray(json)) {
        followups.push(...json.filter((s) => typeof s === 'string'));
      }
    } catch {}
    return '';
  });

  // Charts
  text = text.replace(CHART_REGEX, (_, attrs: string, body: string) => {
    try {
      const type = (extractAttr(attrs, 'type') ?? 'bar') as ChartBlock['type'];
      const title = extractAttr(attrs, 'title') ?? 'Grafico';
      const unit = extractAttr(attrs, 'unit') ?? '';
      const xLabel = extractAttr(attrs, 'xLabel') ?? undefined;
      const yLabel = extractAttr(attrs, 'yLabel') ?? undefined;
      const json = JSON.parse(body.trim());

      if (type === 'trend') {
        // schema diverso: { series: [{name, values}], days?, teamMean? }
        if (json && Array.isArray(json.series)) {
          charts.push({ type, data: { title, unit, ...json } });
        }
        return '';
      }

      if (!Array.isArray(json)) return '';
      const data = json.slice(0, 12);
      switch (type) {
        case 'bar':
        case 'line':
        case 'comparison':
        case 'distribution':
          charts.push({ type, title, unit, data } as ChartBlock);
          break;
        case 'scatter':
          charts.push({ type, title, unit, xLabel, yLabel, data });
          break;
      }
    } catch {}
    return '';
  });

  return { text: text.trim(), charts, athletes, followups };
}

function extractAttr(attrs: string, name: string): string | null {
  const m = attrs.match(new RegExp(`${name}="([^"]+)"`));
  return m ? m[1] : null;
}

const ChartRender: FC<{ chart: ChartBlock }> = ({ chart }) => {
  const yellow = 'var(--c-yellow)';
  const yellowDeep = 'var(--c-yellow-deep)';
  const navy = 'var(--c-navy-1)';
  const text2 = 'var(--c-text-2)';
  const text3 = 'var(--c-text-3)';
  const grid = 'var(--c-card-border)';
  const error = 'var(--c-error)';
  const warning = 'var(--c-warning)';
  const success = 'var(--c-success)';

  if (chart.type === 'trend') {
    return <TrendChart chart={chart.data} />;
  }

  if (chart.type === 'comparison') {
    const max = Math.max(...chart.data.flatMap((d) => [d.current, d.baseline])) * 1.1 || 1;
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartTitle}>{chart.title}</div>
        <div className={styles.comparisonLegend}>
          <span><span className={styles.legendDot} style={{ background: yellow }} /> Attuale</span>
          <span><span className={styles.legendDash} /> Media personale</span>
        </div>
        <div className={styles.comparisonList}>
          {chart.data.map((d) => {
            const curW = (d.current / max) * 100;
            const baseW = (d.baseline / max) * 100;
            const deltaPositive = d.delta >= 0;
            return (
              <div key={d.name} className={styles.comparisonRow}>
                <div className={styles.comparisonRowHead}>
                  <span className={styles.comparisonName}>{d.name}</span>
                  <span className={styles.comparisonMeta}>
                    <strong>{d.current.toFixed(1)}</strong>
                    <span> vs {d.baseline.toFixed(1)} {chart.unit}</span>
                    <span className={deltaPositive ? styles.deltaPos : styles.deltaNeg}>
                      {deltaPositive ? '+' : ''}{d.delta.toFixed(1)}%
                    </span>
                  </span>
                </div>
                <div className={styles.comparisonTrack}>
                  <div className={styles.comparisonBaseline} style={{ left: `${baseW}%` }} />
                  <div className={styles.comparisonBar} style={{ width: `${curW}%` }}>
                    {d.current.toFixed(1)} {chart.unit}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (chart.type === 'distribution') {
    const max = Math.max(...chart.data.map((b) => b.count)) || 1;
    const colorFor = (sev?: string) =>
      sev === 'critical' ? error : sev === 'warning' ? warning : sev === 'good' ? success : yellow;
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartTitle}>{chart.title}</div>
        <div className={styles.distributionBars}>
          {chart.data.map((b) => (
            <div key={b.label} className={styles.distributionBin}>
              <div className={styles.distributionCount}>{b.count}</div>
              <div
                className={styles.distributionBar}
                style={{
                  height: `${(b.count / max) * 100}%`,
                  background: colorFor(b.severity),
                }}
              />
            </div>
          ))}
        </div>
        <div className={styles.distributionLabels}>
          {chart.data.map((b) => (
            <div key={b.label} className={styles.distributionLabel}>
              {b.label}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (chart.type === 'scatter') {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartTitle}>{chart.title}</div>
        <div className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 8, right: 16, left: 8, bottom: 28 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis
                dataKey="x"
                type="number"
                tick={{ fill: text2, fontSize: 11 }}
                label={chart.xLabel ? { value: chart.xLabel, position: 'insideBottom', offset: -8, fill: text2, fontSize: 11 } : undefined}
              />
              <YAxis
                dataKey="y"
                type="number"
                tick={{ fill: text2, fontSize: 11 }}
                label={chart.yLabel ? { value: chart.yLabel, angle: -90, position: 'insideLeft', fill: text2, fontSize: 11 } : undefined}
              />
              <ZAxis range={[60, 160]} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v: any, n: any) => [v, n]} />
              <Scatter
                data={chart.data.filter((d) => !d.highlight)}
                fill={text3}
                fillOpacity={0.55}
              />
              <Scatter
                data={chart.data.filter((d) => d.highlight)}
                fill={error}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // bar | line
  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitle}>{chart.title}</div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === 'line' ? (
            <LineChart data={chart.data} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
              <XAxis dataKey="name" tick={{ fill: text2, fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fill: text2, fontSize: 11 }} unit={chart.unit ? ` ${chart.unit}` : undefined} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke={yellowDeep} strokeWidth={2} dot={{ fill: navy, r: 3 }} />
            </LineChart>
          ) : (
            <BarChart data={chart.data} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
              <XAxis dataKey="name" tick={{ fill: text2, fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fill: text2, fontSize: 11 }} unit={chart.unit ? ` ${chart.unit}` : undefined} />
              <Tooltip />
              <Bar dataKey="value" fill={yellow} radius={[6, 6, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * Render leggero markdown — supporta bold, bullet, paragrafi.
 */
function renderMarkdown(text: string): JSX.Element {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let listBuffer: string[] = [];

  const flushList = (key: string) => {
    if (listBuffer.length === 0) return;
    elements.push(
      <ul key={`ul-${key}`} className={styles.mdList}>
        {listBuffer.map((item, i) => (
          <li key={i}>{renderInline(item)}</li>
        ))}
      </ul>,
    );
    listBuffer = [];
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (/^[-*]\s+/.test(trimmed)) {
      listBuffer.push(trimmed.replace(/^[-*]\s+/, ''));
    } else {
      flushList(String(i));
      if (trimmed === '') {
        elements.push(<div key={`br-${i}`} className={styles.mdSpacer} />);
      } else if (/^#{1,6}\s+/.test(trimmed)) {
        elements.push(
          <h3 key={`h-${i}`} className={styles.mdHeading}>
            {renderInline(trimmed.replace(/^#{1,6}\s+/, ''))}
          </h3>,
        );
      } else {
        elements.push(
          <p key={`p-${i}`} className={styles.mdPara}>
            {renderInline(trimmed)}
          </p>,
        );
      }
    }
  });
  flushList('end');
  return <>{elements}</>;
}

function renderInline(s: string): JSX.Element[] {
  const out: JSX.Element[] = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  const parts = s.split(regex);
  parts.forEach((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) {
      out.push(<strong key={i}>{p.slice(2, -2)}</strong>);
    } else if (/^`[^`]+`$/.test(p)) {
      out.push(
        <code key={i} className={styles.mdInlineCode}>
          {p.slice(1, -1)}
        </code>,
      );
    } else {
      out.push(<span key={i}>{p}</span>);
    }
  });
  return out;
}

export default AiCoachPage;
